/**
 * Export Production results as a ZIP: structured PDF + CSV tables (field + per layer).
 */
import JSZip from 'jszip';

import type { MbFieldAggregate, MbFieldSeriesPoint } from '@/utils/materialBalanceFieldAggregate';
import type { LayerPassportViewModel, PassportKv } from '@/utils/materialBalanceLayerPassport';
import { buildProductionResultsPdfBlob } from '@/utils/materialBalanceResultsPdf';
import { downloadBlob } from '@/utils/downloadBlob';

function csvEscape(v: string | number | null | undefined): string {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCsv(headers: string[], rows: Array<Array<string | number | null | undefined>>): string {
  const lines = [headers.map(csvEscape).join(',')];
  for (const row of rows) {
    lines.push(row.map(csvEscape).join(','));
  }
  return `${lines.join('\n')}\n`;
}

function safeFileSlug(name: string, fallback: string): string {
  const s = name
    .trim()
    .replace(/[^\w.\-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return (s || fallback).slice(0, 80);
}

function kvRows(rows: PassportKv[]): Array<[string, string, string]> {
  return rows.map((r) => [r.label, r.value, r.unit || '']);
}

function fieldProductionCsv(field: MbFieldSeriesPoint[]): string {
  return toCsv(
    [
      'year',
      'oil_kt_yr',
      'liquid_kt_yr',
      'injection_e3m3_yr',
      'np_kt',
      'water_cut_pct',
      'n_prod',
      'n_inj',
    ],
    field.map((r) => [
      r.year,
      round(r.oil, 4),
      round(r.liq, 4),
      round(r.inj, 4),
      round(r.np, 4),
      round(r.fw, 3),
      r.n_prod,
      r.n_inj,
    ]),
  );
}

function layerProductionCsv(passport: LayerPassportViewModel): string {
  return toCsv(
    [
      'year',
      'oil_kt_yr',
      'liquid_kt_yr',
      'injection_e3m3_yr',
      'np_kt',
      'water_cut_pct',
      'recovery_factor',
      'pressure_mpa',
      'n_prod',
      'n_inj',
    ],
    passport.forecast.map((r) => [
      r.year,
      round(r.oil, 4),
      round(r.liq, 4),
      round(r.inj, 4),
      round(r.np, 4),
      r.fw != null ? round(r.fw, 3) : '',
      r.recovery_factor != null ? round(r.recovery_factor, 6) : '',
      r.p != null ? round(r.p, 4) : '',
      r.n_prod ?? '',
      r.n_inj ?? '',
    ]),
  );
}

function layerPropertiesCsv(passport: LayerPassportViewModel): string {
  const rows: Array<[string, string, string]> = [
    ...kvRows(passport.summary),
    ...kvRows(passport.propsRows),
    ...kvRows(passport.diagnostics),
    ...kvRows(passport.relpermSummary),
    ['Producers', String(passport.nProd), ''],
    ['Injectors', String(passport.nInj), ''],
    ['Schedule span', passport.scheduleSpan, ''],
    ['Injection', passport.injectionNote, ''],
  ];
  return toCsv(['parameter', 'value', 'unit'], rows);
}

function layerWellsCsv(passport: LayerPassportViewModel): string {
  return toCsv(
    ['well_name', 'kind', 't0', 't1', 'x_km', 'y_km', 'q_start_td'],
    passport.wells.map((w) => [
      w.well_name,
      w.kind,
      w.t0,
      w.t1,
      w.x != null ? round(w.x, 4) : '',
      w.y != null ? round(w.y, 4) : '',
      w.q_start_td != null ? round(w.q_start_td, 4) : '',
    ]),
  );
}

function round(v: number, digits: number): number {
  const f = 10 ** digits;
  return Math.round(v * f) / f;
}

export type MbResultsExportInput = {
  scenarioId: string;
  agg: MbFieldAggregate;
  passports: LayerPassportViewModel[];
};

/** Build and download a ZIP with structured PDF + CSV tables. */
export async function exportMaterialBalanceResultsZip(
  input: MbResultsExportInput,
): Promise<void> {
  const { scenarioId, agg, passports } = input;
  const zip = new JSZip();
  const stamp = new Date().toISOString().slice(0, 10);
  const shortId = scenarioId.slice(0, 8);

  zip.file(
    'README.txt',
    [
      'QuickField — production results export',
      `Scenario: ${scenarioId}`,
      `Exported: ${new Date().toISOString()}`,
      '',
      'Contents:',
      '  production_results.pdf — structured report (charts drawn from data; not a screen grab)',
      '  tables/field_production.csv — field oil / liquid / injection / Np / water cut / wells',
      '  tables/layers/<name>_properties.csv — reservoir properties & summary KPIs',
      '  tables/layers/<name>_production.csv — per-reservoir forecast series',
      '  tables/layers/<name>_wells.csv — well list for the reservoir',
      '',
    ].join('\n'),
  );

  const pdfBlob = buildProductionResultsPdfBlob({ scenarioId, agg, passports });
  zip.file('production_results.pdf', pdfBlob);

  const tables = zip.folder('tables');
  if (!tables) throw new Error('Failed to create tables folder in archive');

  tables.file('field_production.csv', fieldProductionCsv(agg.field));

  if (agg.extractableOilKt != null || agg.field.length) {
    tables.file(
      'field_summary.csv',
      toCsv(
        ['metric', 'value', 'unit'],
        [
          ['layers_with_result', agg.layersWithResult, ''],
          ['layer_count', agg.layerCount, ''],
          [
            'extractable_oil',
            agg.extractableOilKt != null ? round(agg.extractableOilKt, 3) : '',
            'kt',
          ],
          ['year_min', agg.xDomain ? agg.xDomain[0] : '', ''],
          ['year_max', agg.xDomain ? agg.xDomain[1] : '', ''],
        ],
      ),
    );
  }

  const layersFolder = tables.folder('layers');
  if (!layersFolder) throw new Error('Failed to create layers folder in archive');

  const usedSlugs = new Set<string>();
  for (const p of passports) {
    let slug = safeFileSlug(p.layerName, p.layerId.slice(0, 8));
    if (usedSlugs.has(slug)) slug = `${slug}_${p.layerId.slice(0, 6)}`;
    usedSlugs.add(slug);
    layersFolder.file(`${slug}_properties.csv`, layerPropertiesCsv(p));
    layersFolder.file(`${slug}_production.csv`, layerProductionCsv(p));
    layersFolder.file(`${slug}_wells.csv`, layerWellsCsv(p));
  }

  const archive = await zip.generateAsync({ type: 'blob' });
  downloadBlob(archive, `production-results_${shortId}_${stamp}.zip`);
}
